import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Grid,
    Paper,
    useTheme,
    styled
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const WEEKDAYS = ['Dom', 'Lun', 'Mar', 'Mier', 'Jue', 'Vie', 'Sab'];

const MONTHS = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

interface DayButtonProps {
    selected?: boolean;
    isToday?: boolean;
}

const DayButton = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'isToday'
})<DayButtonProps>(({ theme, selected = false, isToday = false }) => ({
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: '50%',
    fontWeight: isToday ? 500 : 400,
    backgroundColor: selected ? theme.palette.primary.main : 'transparent',
    color: selected ? theme.palette.primary.contrastText : 'inherit',
    '&:hover': {
        backgroundColor: selected ? theme.palette.primary.main : theme.palette.action.hover,
    },
}));

interface CalendarProps {
    initialDate?: Date;
    onDateSelect?: (date: Date) => void;
}

const Calendar: React.FC<CalendarProps> = ({
    initialDate = new Date(),
    onDateSelect
}) => {
    const theme = useTheme();

    const [currentDate, setCurrentDate] = useState(new Date(initialDate));

    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date(initialDate));

    const getDaysInMonth = (year: number, month: number) => {
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (year: number, month: number) => {
        return new Date(year, month, 1).getDay();
    };

    const handlePreviousMonth = () => {
        setCurrentDate(prev => {
            const prevMonth = new Date(prev);
            prevMonth.setMonth(prevMonth.getMonth() - 1);
            return prevMonth;
        });
    };

    const handleNextMonth = () => {
        setCurrentDate(prev => {
            const nextMonth = new Date(prev);
            nextMonth.setMonth(nextMonth.getMonth() + 1);
            return nextMonth;
        });
    };

    const handleDateSelect = (day: number) => {
        const newDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            day
        );

        setSelectedDate(newDate);

        if (onDateSelect) {
            onDateSelect(newDate);
        }
    };

    const renderCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        const daysInMonth = getDaysInMonth(year, month);
        const firstDayOfMonth = getFirstDayOfMonth(year, month);

        const days = [];

        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(null);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            days.push(day);
        }

        const today = new Date();
        const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year;
        const currentDay = today.getDate();

        const weeks: any[][] = [];
        let week: any[] = [];

        days.forEach((day, index) => {
            week.push(day);

            if (index % 7 === 6 || index === days.length - 1) {
                weeks.push(week);
                week = [];
            }
        });

        return (
            <Box>
                <Grid container spacing={0} sx={{ mb: 1 }}>
                    {WEEKDAYS.map((weekday, index) => (
                        <Grid key={index} sx={{ textAlign: 'center', width: '14.28%' }}>
                            <Typography variant="body2" color="text.secondary">
                                {weekday}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>

                {weeks.map((week, weekIndex) => (
                    <Grid container spacing={0} key={weekIndex} sx={{ mb: 2 }}>
                        {week.map((day, dayIndex) => {
                            if (day === null) {
                                return <Grid key={dayIndex} sx={{ width: '14.28%' }} />;
                            }

                            const isSelected = !!selectedDate &&
                                selectedDate.getDate() === day &&
                                selectedDate.getMonth() === month &&
                                selectedDate.getFullYear() === year;

                            const isToday = isCurrentMonth && day === currentDay;

                            return (
                                <Grid key={dayIndex} sx={{ textAlign: 'center', width: '14.28%' }}>
                                    <DayButton
                                        selected={isSelected}
                                        isToday={isToday}
                                        onClick={() => handleDateSelect(day)}
                                    >
                                        {day}
                                    </DayButton>
                                </Grid>
                            );
                        })}
                    </Grid>
                ))}
            </Box>
        );
    };

    return (
        <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <IconButton onClick={handlePreviousMonth} size="small" sx={{ border: '1px solid #e0e0e0' }}>
                    <ChevronLeftIcon />
                </IconButton>

                <Typography variant="h6" fontWeight="bold">
                    {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                </Typography>

                <IconButton onClick={handleNextMonth} size="small" sx={{ border: '1px solid #e0e0e0' }}>
                    <ChevronRightIcon />
                </IconButton>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                }}
            >
                {renderCalendar()}
            </Paper>
        </Box>
    );
};

export default Calendar;